import { useEffect, useState, useCallback } from 'react';

import { VideoPlayer as BaseVideoPlayer } from '@components';
import { StreamingData } from '@common/types/api';

import { useBroadcast } from '../../context/BroadcastContext';
import { TheaterButton } from '../TheaterButton';
import LanguageSelector from './LanguageSelector';
import * as S from './styles';

// Hook para capturar tecla ESC no modo teatro
const useTheaterEscapeKey = (
  theaterMode: boolean,
  toggleTheaterMode: () => void,
) => {
  useEffect(() => {
    if (!theaterMode) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        toggleTheaterMode();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [theaterMode, toggleTheaterMode]);
};

type VideoPlayerProps = {
  src?: string;
  thumb?: string;
  isLive?: boolean;
  streamings?: StreamingData[];
  currentLanguageIndex?: number;
  onLanguageChange?: (index: number) => void;
};

const VideoPlayer = ({
  src,
  thumb,
  isLive = true,
  streamings,
  currentLanguageIndex = 0,
  onLanguageChange,
}: VideoPlayerProps) => {
  const { theaterMode, toggleTheaterMode } = useBroadcast();
  const [controlsLocked, setControlsLocked] = useState(false);

  useTheaterEscapeKey(theaterMode, toggleTheaterMode);

  const handleLanguageSelectorOpenChange = useCallback((open: boolean) => {
    setControlsLocked(open);
  }, []);

  const languageSelector =
    streamings && streamings.length > 1 && onLanguageChange ? (
      <LanguageSelector
        streamings={streamings}
        currentIndex={currentLanguageIndex}
        onSelect={onLanguageChange}
        onOpenChange={handleLanguageSelectorOpenChange}
      />
    ) : null;

  const extraControls = (
    <>
      {languageSelector}
      <TheaterButton
        isTheaterMode={theaterMode}
        onToggle={toggleTheaterMode}
        variant="controls"
      />
    </>
  );

  return (
    <S.Container $theaterMode={theaterMode}>
      {src ? (
        <S.PlayerWrapper>
          <BaseVideoPlayer
            url={src}
            thumbnail={thumb}
            autoPlay
            isLive={isLive}
            controlsLocked={controlsLocked}
            extraControls={extraControls}
          />
        </S.PlayerWrapper>
      ) : (
        <S.Placeholder
          style={thumb ? { backgroundImage: `url(${thumb})` } : undefined}
        />
      )}
    </S.Container>
  );
};

export default VideoPlayer;
